'use client';

import { cn } from '@ui/lib/utils';
import { ChevronDown, Check } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface DropdownOption {
  label: ReactNode;
  value: any;
}

export interface DropdownProps {
  options: DropdownOption[];
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
}

export function Dropdown({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className,
  id,
  disabled = false,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  const handleOptionClick = (optionValue: any) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        className={cn(
          'w-full bg-white border border-gray-300 rounded-md shadow-sm',
          'pl-3 pr-10 py-2 text-left',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          'transition-colors duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'sm:text-sm'
        )}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="block truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </button>

      {isOpen && (
        <div className="absolute mt-1 w-full rounded-md bg-white shadow-lg z-50">
          <ul
            className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
            role="listbox"
          >
            {options.map((option, index) => {
              const isSelected = value === option.value;
              return (
                <li
                  key={`${option.value}-${index}`}
                  className={cn(
                    'cursor-pointer select-none relative py-2 pl-3 pr-9',
                    'hover:bg-primary-600 hover:text-white transition-colors',
                    isSelected ? 'bg-primary-50' : 'text-gray-900'
                  )}
                  onClick={() => handleOptionClick(option.value)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className={cn('font-normal block truncate', isSelected && 'font-medium')}>
                    {option.label}
                  </span>
                  {isSelected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                      <Check className="h-5 w-5 text-primary-600" aria-hidden="true" />
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
